/**
 * Bundle 182 - Copy content from previous event modal (Task 2).
 *
 * Four-step wizard:
 *   1. Pick source event
 *   2. Pick blocks to import (grouped by "reusable defaults" /
 *      "sometimes reusable" / "almost never reusable")
 *   3. Per-block conflict resolution (Skip / Overwrite / Append)
 *   4. Preview + confirm
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Copy, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle,
  Search, X,
} from 'lucide-react';
import { toast } from 'sonner';

const REUSABLE_DEFAULTS = [
  'hosts', 'sponsors', 'faq', 'dress_code', 'food', 'policies',
];
const SOMETIMES_REUSABLE = [
  'hero', 'overview', 'venue', 'schedule', 'what_to_expect',
];
const ALMOST_NEVER_REUSABLE = ['custom_rich_text'];

const BLOCK_LABELS = {
  hero: 'Hero',
  overview: 'Overview',
  venue: 'Venue',
  schedule: 'Schedule',
  what_to_expect: 'What to Expect',
  food: 'Food',
  dress_code: 'Dress Code',
  faq: 'FAQ',
  policies: 'Details (Policies)',
  hosts: 'Hosts',
  sponsors: 'Sponsors',
  custom_rich_text: 'Custom text',
};

const RESOLUTION_LABEL = {
  overwrite: 'Overwrite',
  append: 'Append',
  skip: 'Skip',
};

export function CopyFromPreviousEventDialog({
  open, eventId, onOpenChange, onImported,
}) {
  const [step, setStep] = useState(1);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [checkedTypes, setCheckedTypes] = useState(() => (
    new Set(REUSABLE_DEFAULTS)
  ));
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [resolutions, setResolutions] = useState({});
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setStep(1);
    setSources([]);
    setSelectedSourceId('');
    setCheckedTypes(new Set(REUSABLE_DEFAULTS));
    setShowAllTypes(false);
    setPreview(null);
    setResolutions({});
    setSearch('');
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    setLoadingSources(true);
    api.get(`/admin/events/${eventId}/content-blocks/importable-sources`)
      .then((r) => setSources(r.data?.sources || []))
      .catch((e) => toast.error(
        e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || 'Failed to load sources',
      ))
      .finally(() => setLoadingSources(false));
  }, [open, eventId]);

  const filteredSources = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter((s) => (
      (s.title || '').toLowerCase().includes(q)
      || (s.city || '').toLowerCase().includes(q)
    ));
  }, [sources, search]);

  const selectedSource = useMemo(() => (
    sources.find((s) => s.event_id === selectedSourceId)
  ), [sources, selectedSourceId]);

  const toggleType = (bt) => {
    setCheckedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(bt)) next.delete(bt);
      else next.add(bt);
      return next;
    });
  };

  const goToPreview = async () => {
    const bts = Array.from(checkedTypes).join(',');
    if (!bts) return;
    setLoadingPreview(true);
    try {
      const { data } = await api.get(
        `/admin/events/${eventId}/content-blocks/import-preview`,
        { params: { source_event_id: selectedSourceId, block_types: bts } },
      );
      setPreview(data);
      // Seed per-block resolutions from suggested_resolution.
      const seed = {};
      (data.previews || []).forEach((p) => {
        seed[p.block_type] = p.suggested_resolution || 'skip';
      });
      setResolutions(seed);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Preview failed');
    } finally { setLoadingPreview(false); }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const imports = (preview?.previews || [])
        .filter((p) => p.status === 'ready')
        .map((p) => ({
          block_type: p.block_type,
          resolution: resolutions[p.block_type] || 'skip',
        }));
      const { data } = await api.post(
        `/admin/events/${eventId}/content-blocks/import`,
        { source_event_id: selectedSourceId, imports },
      );
      const succ = data.summary?.success || 0;
      const err = data.summary?.error || 0;
      if (err > 0) {
        toast.error(
          `Imported ${succ}, ${err} failed. Review each block below.`,
        );
      } else {
        toast.success(
          `Imported ${succ} block${succ === 1 ? '' : 's'} from `
          + `${selectedSource?.title || 'source'}. Review + edit each `
          + 'block below.',
        );
      }
      if (onImported) {
        onImported({
          data,
          block_types_imported: (data.results || [])
            .filter((r) => r.status === 'success')
            .map((r) => r.block_type),
        });
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || 'Import failed');
    } finally { setImporting(false); }
  };

  const step1 = (
    <div className="space-y-3" data-testid="copy-step-1">
      <div className="text-sm text-neutral-700">
        Pick a past event to copy content from. Only events with
        copyable blocks are shown.
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2 top-2.5
                                text-neutral-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title or city"
          className="pl-8"
          data-testid="copy-source-search"
        />
      </div>
      <div className="max-h-72 overflow-y-auto border rounded"
                data-testid="copy-source-list">
        {loadingSources ? (
          <div className="p-4 text-xs text-neutral-500">
            Loading events...
          </div>
        ) : filteredSources.length === 0 ? (
          <div className="p-4 text-xs text-neutral-500">
            {sources.length === 0
              ? 'No other events with copyable blocks yet.'
              : 'No events match this filter.'}
          </div>
        ) : (
          filteredSources.map((s) => {
            const isSelected = s.event_id === selectedSourceId;
            return (
              <button
                type="button"
                key={s.event_id}
                onClick={() => setSelectedSourceId(s.event_id)}
                className={
                  'w-full text-left border-t border-neutral-100 '
                  + 'p-3 text-xs '
                  + (isSelected
                    ? 'bg-primary-50 border-primary-200'
                    : 'hover:bg-neutral-50')
                }
                data-testid={`copy-source-row-${s.event_id}`}
              >
                <div className="flex justify-between gap-2">
                  <div className="font-semibold text-primary-900">
                    {s.title}
                  </div>
                  <Badge variant="outline">
                    {s.total_copyable_blocks} blocks
                  </Badge>
                </div>
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  {s.city ? `${s.city} * ` : ''}
                  {s.start_datetime
                    ? new Date(s.start_datetime).toLocaleDateString()
                    : 'no date'}
                  {' * '}
                  <span className="uppercase">{s.status}</span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  const renderTypeCheckbox = (bt) => {
    const has = selectedSource
      ? (selectedSource.block_summary || {})[bt]
      : null;
    const disabled = !has;
    const checked = checkedTypes.has(bt);
    return (
      <label
        key={bt}
        className={
          'flex items-center gap-2 text-xs rounded border p-2 '
          + (disabled
            ? 'opacity-40 bg-neutral-50 cursor-not-allowed'
            : checked
              ? 'bg-primary-50 border-primary-200 cursor-pointer'
              : 'bg-white border-neutral-200 hover:border-primary-300 cursor-pointer')
        }
      >
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-primary-800"
          checked={checked}
          disabled={disabled}
          onChange={() => toggleType(bt)}
          data-testid={`copy-block-type-${bt}`}
        />
        <span className="flex-1 text-primary-900 font-medium">
          {BLOCK_LABELS[bt] || bt}
        </span>
        {has ? (
          <Badge variant="outline" className="text-[9px]">
            {has}
          </Badge>
        ) : (
          <span className="text-[10px] text-neutral-400">n/a</span>
        )}
      </label>
    );
  };

  const step2 = (
    <div className="space-y-3" data-testid="copy-step-2">
      <div className="text-xs text-neutral-700">
        Source: <strong className="text-primary-900">
          {selectedSource?.title || '?'}
        </strong>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest
                              text-neutral-500 mb-1">
          Reusable defaults
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {REUSABLE_DEFAULTS.map(renderTypeCheckbox)}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest
                              text-neutral-500 mb-1">
          Sometimes reusable
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {SOMETIMES_REUSABLE.map(renderTypeCheckbox)}
        </div>
      </div>
      <div>
        <button
          type="button"
          onClick={() => setShowAllTypes((v) => !v)}
          className="text-[11px] text-primary-800 underline"
          data-testid="copy-show-all-toggle"
        >
          {showAllTypes
            ? 'Hide almost never reusable'
            : 'Show all block types'}
        </button>
        {showAllTypes ? (
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {ALMOST_NEVER_REUSABLE.map(renderTypeCheckbox)}
          </div>
        ) : null}
      </div>
    </div>
  );

  const step3 = (
    <div className="space-y-3" data-testid="copy-step-3">
      <div className="text-xs text-neutral-700">
        Resolve conflicts. Suggested resolution is prefilled per
        block.
      </div>
      <div className="max-h-72 overflow-y-auto border rounded">
        <table className="w-full text-xs"
                    data-testid="copy-conflict-table">
          <thead>
            <tr className="bg-neutral-50 text-left">
              <th className="p-2">Block</th>
              <th className="p-2">Target</th>
              <th className="p-2">Action</th>
              <th className="p-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {(preview?.previews || []).map((p) => (
              <tr key={p.block_type}
                        className="border-t border-neutral-100 align-top"
                        data-testid={`copy-conflict-row-${p.block_type}`}>
                <td className="p-2">
                  <div className="font-medium text-primary-900">
                    {BLOCK_LABELS[p.block_type] || p.block_type}
                  </div>
                  <div className="text-[10px] text-neutral-500">
                    src: {p.items_count} item{p.items_count === 1 ? '' : 's'}
                  </div>
                </td>
                <td className="p-2">
                  {p.status === 'source_missing' ? (
                    <span className="text-[10px] text-neutral-400">
                      no source
                    </span>
                  ) : p.conflict ? (
                    <Badge className="bg-amber-50 text-amber-800
                                             border border-amber-200">
                      has content ({p.target_items_count})
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-50 text-emerald-800
                                             border border-emerald-200">
                      empty
                    </Badge>
                  )}
                </td>
                <td className="p-2">
                  {p.status === 'source_missing' ? (
                    <span className="text-[10px] text-neutral-400">
                      -
                    </span>
                  ) : (
                    <select
                      className="text-xs border rounded px-1 py-0.5"
                      value={resolutions[p.block_type] || 'skip'}
                      onChange={(e) => setResolutions((prev) => ({
                        ...prev,
                        [p.block_type]: e.target.value,
                      }))}
                      data-testid={`copy-resolution-${p.block_type}`}
                    >
                      <option value="overwrite">Overwrite</option>
                      <option value="append"
                                    disabled={p.action_hint
                                      && !p.action_hint.includes('append')
                                      && !p.action_hint.includes('items')}>
                        Append
                      </option>
                      <option value="skip">Skip</option>
                    </select>
                  )}
                </td>
                <td className="p-2 text-[10px] text-neutral-600">
                  {p.action_hint || p.message || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const summaryLines = useMemo(() => {
    if (!preview) return [];
    return (preview.previews || [])
      .filter((p) => p.status === 'ready')
      .map((p) => {
        const res = resolutions[p.block_type] || 'skip';
        if (res === 'skip') {
          return `${BLOCK_LABELS[p.block_type]}: skip`;
        }
        if (res === 'append') {
          return (
            `${BLOCK_LABELS[p.block_type]}: append ${p.items_count} `
            + `item${p.items_count === 1 ? '' : 's'} to existing `
            + `${p.target_items_count}`
          );
        }
        return (
          `${BLOCK_LABELS[p.block_type]}: overwrite `
          + (p.conflict ? 'existing content' : 'empty target')
          + ` (${p.items_count} item${p.items_count === 1 ? '' : 's'})`
        );
      });
  }, [preview, resolutions]);

  const step4 = (
    <div className="space-y-3" data-testid="copy-step-4">
      <div
        className="rounded-md border border-primary-200
                        bg-primary-50 p-3 text-xs text-primary-900"
      >
        Ready to import from{' '}
        <strong>{selectedSource?.title || 'source'}</strong>. Review
        the plan below.
      </div>
      <ul className="text-xs text-primary-900 list-disc pl-5"
                data-testid="copy-summary-list">
        {summaryLines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <div
        className="rounded-md border border-amber-200 bg-amber-50 p-3
                        text-xs text-amber-900 flex gap-2"
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          Source event is not modified. Imported blocks are
          highlighted on the Content tab. Event-specific fields
          (dates, slug, event id) are stripped automatically.
        </div>
      </div>
    </div>
  );

  const nextDisabled = (() => {
    if (step === 1) return !selectedSourceId;
    if (step === 2) return checkedTypes.size === 0;
    if (step === 3) {
      const willImport = (preview?.previews || [])
        .filter((p) => p.status === 'ready')
        .some((p) => (
          (resolutions[p.block_type] || 'skip') !== 'skip'
        ));
      return !willImport;
    }
    return false;
  })();

  const renderStep = () => {
    if (step === 1) return step1;
    if (step === 2) return step2;
    if (step === 3) return step3;
    return step4;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="copy-from-previous-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            Copy content from previous event
            <span className="ml-2 text-[10px] text-neutral-500
                                 uppercase tracking-widest">
              Step {step} of 4
            </span>
          </DialogTitle>
        </DialogHeader>
        {renderStep()}
        <DialogFooter>
          <div className="flex-1 text-[10px] text-neutral-500">
            {step === 4
              ? 'Nothing writes until you click Import.'
              : ''}
          </div>
          {step > 1 && step < 4 ? (
            <Button
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              data-testid="copy-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          ) : null}
          {step === 1 || step === 2 ? (
            <Button
              onClick={() => {
                if (step === 2) { goToPreview(); }
                else setStep((s) => s + 1);
              }}
              disabled={nextDisabled || loadingPreview}
              className="bg-primary-800 text-white hover:bg-primary-900"
              data-testid="copy-next-btn"
            >
              {loadingPreview ? 'Loading...' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : null}
          {step === 3 ? (
            <Button
              onClick={() => setStep(4)}
              disabled={nextDisabled}
              className="bg-primary-800 text-white hover:bg-primary-900"
              data-testid="copy-review-btn"
            >
              Review plan
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : null}
          {step === 4 ? (
            <>
              <Button
                variant="outline"
                onClick={() => setStep(3)}
                data-testid="copy-edit-plan-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Edit plan
              </Button>
              <Button
                onClick={doImport}
                disabled={importing}
                className="bg-emerald-700 text-white hover:bg-emerald-800"
                data-testid="copy-import-confirm-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {importing ? 'Importing...' : 'Import blocks'}
              </Button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CopyFromPreviousEventDialog;
